import NavBar from '../components/NavBar';
import { useLanguage } from '../i18n/LanguageContext';

const HelpCenter = ({ user, onLogout }) => {
  const { t } = useLanguage();

  const sections = [
    {
      icon: 'fa-rocket',
      title: 'Getting Started',
      items: [
        { q: 'How do I set up my check-in schedule?', a: 'Go to Profile → Edit Profile → set your Check-in Interval (daily, every few days, or weekly). Your emergency contacts will be notified if you miss a scheduled check-in.' },
        { q: 'How do I add emergency contacts?', a: 'Go to Profile → Edit Profile → scroll to Emergency Contacts. Add their name, phone, email, and relationship. They will be notified if you miss check-ins.' },
        { q: 'How do I add clients?', a: 'Go to Clients → Add New Client. Enter their name, address, health conditions, and emergency contacts.' }
      ]
    },
    {
      icon: 'fa-bell',
      title: 'Check-ins & Alerts',
      items: [
        { q: 'What happens when I check in?', a: 'Your check-in timer resets, and your status shows as "OK" to the admin. You can add how you\'re feeling and any health concerns.' },
        { q: 'What if I miss a check-in?', a: 'Your emergency contacts will be notified via SMS, phone call, and/or email based on your notification settings. If still unreachable, 911 can be contacted automatically.' },
        { q: 'What is a Yellow Alert?', a: 'Yellow Alert means "I\'m not feeling great but I\'m okay." It shortens your check-in window so you\'re monitored more closely.' },
        { q: 'What is a Red Alert?', a: 'Red Alert means "Check on me in a few hours." It triggers a shorter check-in window and notifies contacts if you don\'t respond.' }
      ]
    },
    {
      icon: 'fa-calendar-days',
      title: 'Calendar & Reminders',
      items: [
        { q: 'How do I add an appointment?', a: 'Go to Calendar → click "Add Event" on any day. Set the title, type (Appointment, Task, Medication), time, and optional reminder.' },
        { q: 'How does the timer work?', a: 'Go to Calendar → Timer tab. Set hours, minutes, seconds, and click Start. You\'ll get a notification when it finishes.' },
        { q: 'How do I set an alarm?', a: 'Go to Calendar → Alarm tab. Set a time and label, click the + button. Alarms will ring even if you navigate to other pages.' }
      ]
    },
    {
      icon: 'fa-file-lines',
      title: 'Documents & Vitals',
      items: [
        { q: 'What documents should I upload?', a: 'We recommend uploading: Last Will & Testament, Healthcare Directive, Living Trust, and Power of Attorney. The Document Checklist will guide you.' },
        { q: 'Are my documents secure?', a: 'Yes. Documents are stored securely and only accessible to you. Consider sharing access info with trusted emergency contacts.' },
        { q: 'How do I track my vitals?', a: 'Go to Vitals → Record Vitals. Track blood pressure, heart rate, temperature, blood sugar, weight, and oxygen saturation.' }
      ]
    },
    {
      icon: 'fa-life-ring',
      title: 'Emergency Procedures',
      items: [
        { q: 'What is the escalation process?', a: '1) You miss a check-in → 2) You receive SMS/call/email reminders → 3) Your emergency contacts are notified → 4) If still unreachable and enabled, 911 is called for a wellness check.' },
        { q: 'Can I test the emergency system?', a: 'You can trigger a Yellow Alert from your Dashboard to test a shortened check-in window without alarming your contacts.' },
        { q: 'How do I update my emergency number?', a: 'Go to Profile → Edit Profile → scroll to the emergency number field at the bottom of the page.' }
      ]
    }
  ];

  return (
    <div className="dashboard-container" data-testid="help-center-page">
      {user ? <NavBar onLogout={onLogout} /> : (
        <nav className="dashboard-nav">
          <a href="/"><img src="/careguard-logo.png" alt="CareGuard" className="nav-logo" /></a>
          <div className="nav-links"><a href="/auth" className="nav-link">Login</a></div>
        </nav>
      )}
      <div className="dashboard-content page-container" style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h1 className="page-title" data-testid="help-title">
          <i className="fa-solid fa-circle-question" style={{ marginRight: '10px', color: '#2c5364' }}></i>Help Center
        </h1>
        <p style={{ color: '#6b8899', marginBottom: '2rem', fontSize: '1rem' }}>
          Everything you need to know about using CareGuard to stay safe and connected.
        </p>

        {sections.map((section, si) => (
          <div key={si} className="section-card" style={{ marginBottom: '1.5rem' }} data-testid={`help-section-${si}`}>
            <h2 style={{ color: '#2c5364', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className={`fa-solid ${section.icon}`} style={{ color: '#2c5364' }}></i> {section.title}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {section.items.map((item, qi) => (
                <details key={qi} style={{ borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <summary style={{
                    padding: '0.75rem 1rem', cursor: 'pointer', fontWeight: '600', color: '#2c5364',
                    background: '#f8f9fa', fontSize: '0.95rem', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem'
                  }}>
                    <i className="fa-solid fa-chevron-right" style={{ fontSize: '0.7rem', transition: 'transform 0.2s' }}></i>
                    {item.q}
                  </summary>
                  <div style={{ padding: '0.75rem 1rem 1rem', color: '#4a6572', fontSize: '0.9rem', lineHeight: '1.6', background: '#fff' }}>
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        ))}

        <div style={{ background: '#e7f3ff', padding: '1.25rem', borderRadius: '8px', borderLeft: '4px solid #2c5364', marginTop: '1rem' }}>
          <p style={{ margin: 0, color: '#0c5460' }}>
            <strong><i className="fa-solid fa-headset" style={{ marginRight: '6px' }}></i>Need more help?</strong> Send us a message from the <a href="/messages" style={{ color: '#2c5364', fontWeight: '600' }}>Messages</a> page or email <strong>support@careguardapp.com</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
